import React from 'react';
import { FeatureProvider, FEATURE_DEFAULTS, mergeFeatureFlags } from '@caro/shared/features';
import type { FeatureFlags } from '@caro/shared/features';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';

/**
 * Seeds the shared `<FeatureProvider>` for the resident-app (R2 / T034).
 *
 * Spec: specs/20260602-resident-device-autoprovision/plan.md §5 R2
 *
 * Reads the building's merged effective feature flags from the
 * `getBuildingConfig` response (`building.features` — the server already merges
 * code defaults ⊕ `location.app_config.features` and forces `emergency` on via
 * `mergeFeatureFlags`). While the config is loading (or when unauthenticated /
 * no tenant), we seed with the pure code defaults so the UI never gates off a
 * feature spuriously before the per-building override arrives — and `emergency`
 * is always on regardless.
 */
interface BuildingConfigResponse {
  building?: { features?: Partial<FeatureFlags> };
}

export function BuildingFeaturesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { data } = useApi<BuildingConfigResponse>(
    user?.tenantId ? `/api/v1/building/${user.tenantId}/config` : null,
  );

  // The server returns already-merged flags; re-merging over the code defaults
  // is idempotent and guarantees a complete map (+ emergency on) even if the
  // response is partial or not yet loaded.
  const features = mergeFeatureFlags(FEATURE_DEFAULTS, data?.building?.features);

  return <FeatureProvider features={features}>{children}</FeatureProvider>;
}
